import { Component, OnInit } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { forkJoin, Subscription } from 'rxjs';
import { sortAscSmallIcon, sortDescSmallIcon } from '@progress/kendo-svg-icons';
import { ExpenseTypeService } from 'src/app/expenses/services/expense-type.service';
import { ExpenseTypeKind, IExpenseType } from 'src/app/expenses/models/expense-type';
import { NameValidators } from 'src/app/shared/validators/name.validators';

@Component({
    selector: 'app-expense-types',
    templateUrl: './expense-types.component.html',
    standalone: false
})
export class ExpenseTypesComponent implements OnInit {

  expenseTypes: IExpenseType[] = [];
  formGroup!: UntypedFormGroup;
  private editedRowIndex!: number;
  private typeChangeSub?: Subscription;

  ExpenseTypeKind = ExpenseTypeKind;
  kindOptions = [
    { text: 'KmExpense', value: ExpenseTypeKind.KmExpense },
    { text: 'CashExpense', value: ExpenseTypeKind.CashExpense }
  ];

  icons = {
    sortAsc: sortAscSmallIcon,
    sortDesc: sortDescSmallIcon
  };

  constructor(private expenseTypeService: ExpenseTypeService) { }

  ngOnInit(): void {
    this.load();
  }

  load() {
    this.expenseTypeService.searchExpenseTypes().subscribe(response => {
      this.expenseTypes = response.data;
    });
  }

  kindName(type: ExpenseTypeKind): string {
    return ExpenseTypeKind[type];
  }

  addHandler({ sender }) {
    this.closeEditor(sender);
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(null, NameValidators(() => this.expenseTypes.map(e => e.name), 1024, true)),
      type: new UntypedFormControl(ExpenseTypeKind.KmExpense, Validators.required),
      kmRate: new UntypedFormControl(null),
      flatrate: new UntypedFormControl(null),
      salaryPayment: new UntypedFormControl(false),
      requiresReceipt: new UntypedFormControl(false)
    });
    this.bindTypeDependency(this.formGroup);
    sender.addRow(this.formGroup);
  }

  editHandler({ sender, rowIndex, dataItem }) {
    this.closeEditor(sender);
    this.formGroup = new UntypedFormGroup({
      name: new UntypedFormControl(dataItem.name,
        NameValidators(() => this.expenseTypes.filter(e => e.id !== dataItem.id).map(e => e.name), 1024, true)),
      type: new UntypedFormControl(dataItem.type, Validators.required),
      kmRate: new UntypedFormControl(dataItem.kmRate),
      flatrate: new UntypedFormControl(dataItem.flatrate),
      salaryPayment: new UntypedFormControl(dataItem.salaryPayment ?? false),
      requiresReceipt: new UntypedFormControl(dataItem.requiresReceipt ?? false)
    });
    this.bindTypeDependency(this.formGroup);
    this.editedRowIndex = rowIndex;
    sender.editRow(rowIndex, this.formGroup);
  }

  cancelHandler({ sender, rowIndex }) {
    this.closeEditor(sender, rowIndex);
  }

  saveHandler(event) {
    const value = event.formGroup.value;
    const isKm = value.type === ExpenseTypeKind.KmExpense;
    const id = event.dataItem.id ?? null;
    const request = {
      id,
      name: value.name?.trim(),
      type: value.type,
      kmRate: isKm ? value.kmRate : null,
      flatrate: !isKm ? value.flatrate : null,
      salaryPayment: value.salaryPayment ?? false,
      requiresReceipt: value.requiresReceipt ?? false,
      // New types go to the end; existing ones keep their order (the backend would otherwise store 0).
      sortOrder: id ? (event.dataItem.sortOrder ?? 0) : this.expenseTypes.length
    };

    const save$ = id
      ? this.expenseTypeService.updateExpenseType(request)
      : this.expenseTypeService.createExpenseType(request);

    // Close the row only on success, so the input survives a rejected save (errors are shown by the interceptor).
    save$.subscribe(() => {
      event.sender.closeRow(event.rowIndex);
      this.load();
    });
  }

  deletionDialogOpened = false;
  private itemToDelete: { id: string } | null = null;

  removeHandler({ dataItem }) {
    this.itemToDelete = dataItem;
    this.deletionDialogOpened = true;
  }

  deleteConfirmed() {
    const dataItem = this.itemToDelete;
    this.itemToDelete = null;
    if (!dataItem) return;
    this.expenseTypeService.deleteExpenseType(dataItem.id).subscribe(() => this.load());
  }

  onMoveUp(dataItem: IExpenseType) {
    this.move(dataItem, +1);
  }

  onMoveDown(dataItem: IExpenseType) {
    this.move(dataItem, -1);
  }

  // Swap sortOrder with the neighbour at the target position so the order stays free of duplicates.
  private move(dataItem: IExpenseType, delta: number) {
    const target = dataItem.sortOrder + delta;
    const neighbour = this.expenseTypes.find(e => e.id !== dataItem.id && e.sortOrder === target);

    const updates = [
      this.expenseTypeService.updateExpenseType({ ...dataItem, sortOrder: target }),
    ];
    if (neighbour) {
      updates.push(this.expenseTypeService.updateExpenseType({ ...neighbour, sortOrder: dataItem.sortOrder }));
    }

    forkJoin(updates).subscribe(() => this.load());
  }

  // kmRate is only relevant for Fahrspesen, flatrate only for Fester Betrag —
  // keep the irrelevant field disabled and react to changes of the selected type.
  private bindTypeDependency(formGroup: UntypedFormGroup) {
    this.typeChangeSub?.unsubscribe();
    this.syncFieldStates(formGroup);
    this.typeChangeSub = formGroup.get('type').valueChanges
      .subscribe(() => this.syncFieldStates(formGroup));
  }

  private syncFieldStates(formGroup: UntypedFormGroup) {
    const isKm = formGroup.get('type').value === ExpenseTypeKind.KmExpense;
    const kmRate = formGroup.get('kmRate');
    const flatrate = formGroup.get('flatrate');

    if (isKm) {
      kmRate.enable({ emitEvent: false });
      flatrate.disable({ emitEvent: false });
    } else {
      kmRate.disable({ emitEvent: false });
      flatrate.enable({ emitEvent: false });
    }
  }

  private closeEditor(grid, rowIndex = this.editedRowIndex) {
    grid.closeRow(rowIndex);
    this.typeChangeSub?.unsubscribe();
    this.typeChangeSub = undefined;
    this.editedRowIndex = undefined;
    this.formGroup = undefined;
  }
}
